import { buyColor } from "../../assets/colors";
import React, { useContext, useMemo } from "react";
import {
    Box,
    FormGroup,
    Typography,
    FormControl,
    TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { CandlestickSeriesOptions, LineBreakSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import CommonChartSettingsTabs from './commonChartSettingsTabs';
import { chartSettingsFormGroupSx, chartSettingsRowSx } from './chartSettingsLayout';

type Props = {
    viewKey: string;
};

const LineBreakSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])


    const upColor = useMemo(() => {
        let color = buyColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColor ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])



    const borderUpColor = useMemo(() => {
        let color = buyColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderUpColor ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const borderDownColor = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])


    const lineCount = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as LineBreakSeriesOptions
            return series.lineCount ?? 3
        }
        return 3
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof LineBreakSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<CandlestickSeriesOptions>);
        }
    }

    const handleLineCountChange = (value: number) => {
        if (chart && value > 0) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                lineCount: value
            } as LineBreakSeriesOptions)
        }
    }

    const symbolTabContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsRowSx}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Up bars</Typography>
                    <ColorSelector compact showOnlySwatch color={upColor.toHexString()} opacity={upColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'upColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={borderUpColor.toHexString()} opacity={borderUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'borderUpColor');
                        }} />
                </Box>
                <Box sx={chartSettingsRowSx}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Down bars</Typography>
                    <ColorSelector compact showOnlySwatch color={downColor.toHexString()} opacity={downColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'downColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={borderDownColor.toHexString()} opacity={borderDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'borderDownColor');
                        }} />
                </Box>


                <Box sx={chartSettingsRowSx}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Line Count</Typography>
                    <FormControl size="small">
                        <TextField
                            fullWidth={false}
                            variant="outlined"
                            size="small"
                            value={lineCount}
                            onChange={(event) => handleLineCountChange(Number(event.target.value))}
                            type="number"
                            sx={{
                                width: '80px',
                                '& .MuiInputBase-root': {
                                    height: '32px'
                                }
                            }}
                        />
                    </FormControl>
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default LineBreakSettings;
